import { Alert, Box, Button, CircularProgress, Dialog, Divider, Paper, Stack, TextField, Typography } from "@mui/material"
import { ROTULO_CONSUMO, useGetFormasPagamentoLojaQuery, useGetLojaAtualQuery, type FormaPagamentoLoja, } from "@/api"
import { cpfDoPedidoTotem, passosDoTotem, ROTULO_PASSO_TOTEM, ultimoPassoDoTotem, type RespostaCpfNota, } from "@/pages/checkout-final/passos-totem"
import { formasDoTotem, passoValido, podeEnviar, trocoPendente, type DadosFinalizacao } from "./estado"
import { imprimeNoAparelho, opcoesImpressaoTotem, perguntaCpfNaNota } from "@/lib/impressao-totem"
import { useEffect, useMemo, useRef, useState } from "react"
import { descreverTroco, lerValorTroco } from "@/lib/troco"
import { irVista, reiniciarAtendimento } from "../navegacao"
import { useDispatch, useSelector } from "react-redux"
import { getLocalImpressaoTotem } from "@/lib/totem-config"
import { montarPayloadPedido } from "@/lib/payload-pedido"
import { DialogPagamentoTef } from "@/components/dialog-pagamento-tef"
import { useEnviarPedido } from "@/lib/enviar-pedido"
import type { RootState } from "@/store"
import ScreenHeader from "@/pages/totem/checkout/components/screen-header"
import LayoutPassos, { useTelaDeitada } from "@/pages/totem/checkout/components/layout-passos"
import StepperPagamento from "@/pages/totem/checkout/payment-screen/stepper-pagamento"
import ListaFormas from "@/pages/totem/checkout/payment-screen/lista-formas"
import { IconePagamento } from "@/components/icone-pagamento"
import { metodosDaForma } from "@/lib/tef/metodos-da-forma"
import { formatarBRL } from "@/lib/formato"
import { canalDoIdentificador } from "@/lib/canal"
import { useReconciliacaoCarrinho } from "@/lib/usar-reconciliacao-carrinho"
import { avisoDeMudancaDePreco } from "@/lib/reconciliar-carrinho"
import { DialogTroco } from "@/components/dialog-troco"
import { PassoPager } from "@/pages/checkout-final/passo-pager"
import { LgpdAviso } from "@/components/lgpd-aviso"
import HomeIcon from "@mui/icons-material/Home"
import CpfInput from "@/pages/totem/checkout/payment-screen/pos-pagamento/cpf-input"
import CpfPergunta from "@/pages/totem/checkout/payment-screen/pos-pagamento/cpf-pergunta"
import { mensagemDoErro } from "@/lib/erro-api"
import PersonIcon from "@mui/icons-material/Person"
import RestaurantIcon from "@mui/icons-material/Restaurant"
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive"

// Mesmo botão grande e arredondado das telas do checkout.
const ESTILO_ACAO = { minHeight: 64, borderRadius: 3, textTransform: 'none', fontSize: '1.4rem', fontWeight: 'bold' } as const

function TituloPasso({ children }: { children: string }): JSX.Element {
    return (
        <Typography variant="h3" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            {children}
        </Typography>
    )
}

export default function Finalizar(): JSX.Element {
    const dispatch = useDispatch()
    const { itens, observacaoGeral } = useSelector((s: RootState) => s.carrinho)
    const consumo = useSelector((s: RootState) => s.totemPedidos.consumo)
    const { data: loja } = useGetLojaAtualQuery()
    const { data: formasPagamento } = useGetFormasPagamentoLojaQuery(undefined, { skip: !loja })
    const { enviar, enviando } = useEnviarPedido()
    const { alteracoes, descartarAviso } = useReconciliacaoCarrinho(loja ? canalDoIdentificador(loja.identificador) : 'CARDAPIO_TOTEM',)
    const avisoPreco = avisoDeMudancaDePreco(alteracoes, formatarBRL)
    const [passo, setPasso] = useState(0)
    const [nome, setNome] = useState('')
    const [cpf, setCpf] = useState('')
    const [pager, setPager] = useState('')
    const [pagerConferido, setPagerConferido] = useState(false)
    const [respostaCpfNota, setRespostaCpfNota] = useState<RespostaCpfNota>(null)
    const [digitandoCpfNota, setDigitandoCpfNota] = useState(false)
    const [idForma, setIdForma] = useState<number | ''>('')
    const [trocoInput, setTrocoInput] = useState('')
    const [trocoRespondido, setTrocoRespondido] = useState(false)
    const [trocoAberto, setTrocoAberto] = useState(false)
    const [tefAberto, setTefAberto] = useState<{ pedidoId: string; valorCentavos: number; metodos: ReturnType<typeof metodosDaForma> } | null>(null)
    const [erro, setErro] = useState<string | null>(null)
    const [falhaEnvio, setFalhaEnvio] = useState(false)

    // A resposta da nota vale para o CPF de quando foi dada: trocou o CPF, pergunta de novo.
    useEffect(() => setRespostaCpfNota(null), [cpf])
    const usaPager = loja?.configuracao.totemPagerAtivo === true
    // Sem a pergunta (o lojista desligou), a nota leva só o CPF que o cliente der na identificação.
    const perguntaCpfNota = opcoesImpressaoTotem(loja?.configuracao).nota !== 'NAO' && perguntaCpfNaNota(loja?.configuracao)
    const passosVisiveis = useMemo(
        () => passosDoTotem(!!usaPager, perguntaCpfNota).filter((p) => p !== 'CONSUMO'),
        [usaPager, perguntaCpfNota],
    )
    const ultimoPasso = ultimoPassoDoTotem(passosVisiveis, !!usaPager)
    const formas = formasDoTotem(formasPagamento)
    const formaSelecionada = formas.find((fp) => fp.id === idForma)
    const subtotal = itens.reduce((s, item) => s + item.precoUnitario * item.quantidade, 0)
    const totalItens = itens.reduce((s, item) => s + item.quantidade, 0)
    const deitado = useTelaDeitada()
    const trocoValor = lerValorTroco(trocoInput)

    const dados: DadosFinalizacao = {
        nome,
        pager,
        pagerConferido,
        respostaCpfNota,
        formaSelecionada,
        trocoRespondido,
        trocoValor,
        total: subtotal,
    }

    const trocoTravado = trocoPendente(dados)
    const refNome = useRef<HTMLInputElement>(null)

    const fecharParaCarrinho = (): void => irVista(dispatch, { currentView: 'VIEW_CARRINHO' })

    // A seta do cabeçalho volta um passo; no primeiro, volta ao carrinho.
    function voltar(): void {
        setDigitandoCpfNota(false)
        if (passo > 0) setPasso((p) => p - 1)
        else fecharParaCarrinho()
    }

    function escolherForma(fp: FormaPagamentoLoja): void {
        if (fp.forma === 'DINHEIRO') {
            if (idForma !== fp.id) setTrocoRespondido(false)
            setTrocoAberto(true)
        }
        setIdForma(fp.id)
    }

    async function confirmar(): Promise<void> {
        setErro(null)
        if (!loja) return

        const payload = montarPayloadPedido({
            cliente: {
                nome: nome.trim(),
                telefone: '',
                cpf: cpfDoPedidoTotem(perguntaCpfNota, respostaCpfNota, cpf),
            },
            itens,
            observacaoGeral: observacaoGeral?.trim() || undefined,
            consumoTotem: consumo ?? undefined,
            impressaoTotem: { imprimeNoAparelho: imprimeNoAparelho(), localImpressao: getLocalImpressaoTotem() },
            codigoPager: usaPager ? pager : undefined,
            formaPagamento: formaSelecionada ?? null,
            troco: formaSelecionada?.forma === 'DINHEIRO' && trocoValor !== null ? trocoValor : undefined,
            subtotal,
            taxaEntrega: 0,
            desconto: 0,
            total: subtotal,
        })

        try {
            const desfecho = await enviar({
                payload,
                cobraAntes: formaSelecionada?.tipo === 'TEF',
            })

            if (desfecho.tipo === 'cancelado') {
                setErro('Este pedido foi cancelado e não pode mais ser pago. Toque em Enviar pedido para fazer um novo.')
                return
            }

            if (desfecho.tipo === 'cobrar') {
                setTefAberto({
                    pedidoId: desfecho.pedido.id,
                    valorCentavos: Math.round(subtotal * 100),
                    metodos: metodosDaForma(formaSelecionada!.forma, formaSelecionada!.metodos),
                })
                return
            }

            irVista(dispatch, { currentView: 'VIEW_CONFIRMADO', pedidoId: desfecho.pedido.id })
        } catch (err) {
            setErro(mensagemDoErro(err) ?? 'Não foi possível confirmar o pedido')
            setFalhaEnvio(true)
        }
    }

    useEffect(() => {
        if (passosVisiveis[passo] !== 'IDENTIFICACAO') return

        const t = setTimeout(() => refNome.current?.focus(), 150)

        return () => clearTimeout(t)
    }, [passo, passosVisiveis])

    const passoAtual = passosVisiveis[passo]!
    const perguntarTroco = passoAtual === 'PAGAMENTO' && trocoTravado

    const conteudo = (() => {
        switch (passoAtual) {
            case 'IDENTIFICACAO':
                return (
                    <Stack spacing={3} data-form-teclado sx={{ width: '100%', maxWidth: 650 }}>
                        <TituloPasso>Como podemos te chamar?</TituloPasso>
                        <TextField
                            label="Nome"
                            value={nome}
                            onChange={(e) => setNome(e.target.value)}
                            fullWidth
                            required
                            autoFocus
                            autoComplete="name"
                            inputRef={refNome}
                            inputProps={{ enterKeyHint: 'next' }}
                        />
                        <TextField
                            label="CPF (opcional)"
                            value={cpf}
                            onChange={(e) => setCpf(e.target.value)}
                            fullWidth
                            inputProps={{ inputMode: 'numeric', enterKeyHint: 'done' }}
                        />
                        <LgpdAviso />
                    </Stack>
                )

            case 'PAGER':
                return (
                    <Box sx={{ width: '100%', maxWidth: 650 }}>
                        <PassoPager
                            valor={pager}
                            onChange={setPager}
                            minimo={loja?.configuracao.totemPagerMinimo ?? null}
                            maximo={loja?.configuracao.totemPagerMaximo ?? null}
                            onConferenciaChange={setPagerConferido}
                        />
                    </Box>
                )

            case 'CPF_NOTA':
                return (
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                        {digitandoCpfNota ? (
                            <CpfInput
                                valorInicial={cpf}
                                onConfirmar={(informado) => {
                                    setRespostaCpfNota({ incluir: true, cpf: informado })
                                    setPasso((p) => p + 1)
                                }}
                                onVoltar={() => setDigitandoCpfNota(false)}
                            />
                        ) : (
                            <CpfPergunta
                                onInformar={() => setDigitandoCpfNota(true)}
                                onPular={() => {
                                    setRespostaCpfNota({ incluir: false, cpf: '' })
                                    setPasso((p) => p + 1)
                                }}
                            />
                        )}
                    </Box>
                )

            case 'PAGAMENTO':
                return (
                    <Stack spacing={4} sx={{ alignItems: 'center', width: '100%' }}>
                        <Stack spacing={0.5} sx={{ alignItems: 'center' }}>
                            <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                                Total a pagar
                            </Typography>
                            <Typography variant="h1" sx={{ color: 'success.main', fontWeight: '900', fontSize: '58px' }}>
                                {formatarBRL(subtotal)}
                            </Typography>
                        </Stack>

                        {formas.length === 0 ? (
                            <Alert severity="warning" sx={{ width: '100%', maxWidth: 650, fontSize: '1.1rem' }}>
                                A loja ainda não configurou as formas de pagamento. Chame um atendente.
                            </Alert>
                        ) : (
                            <ListaFormas
                                formas={formas}
                                onEscolher={escolherForma}
                                colunas={deitado ? 2 : 1}
                                selecionada={idForma === '' ? null : idForma}
                                detalheDaSelecionada={formaSelecionada?.forma === 'DINHEIRO' && trocoRespondido ? descreverTroco(trocoValor, subtotal) : null}
                            />
                        )}
                    </Stack>
                )

            case 'CONFIRMACAO':
                return (
                    <Stack spacing={3} sx={{ alignItems: 'center', width: '100%', maxWidth: 560 }}>
                        <TituloPasso>Confira seu pedido</TituloPasso>

                        <Paper variant="outlined" sx={{ width: '100%', p: 3, borderRadius: 3 }}>
                            <Stack spacing={2.5} divider={<Divider flexItem />}>
                                <Stack spacing={2}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        {consumo === 'LEVAR' ? <ShoppingBagIcon color="action" /> : <RestaurantIcon color="action" />}
                                        <Typography variant="h6" fontWeight={600}>{consumo ? ROTULO_CONSUMO[consumo] : '—'}</Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <PersonIcon color="action" />
                                        <Typography variant="h6" fontWeight={600}>{nome.trim() || '—'}</Typography>
                                    </Stack>
                                    {usaPager && pager.trim() && (
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <NotificationsActiveIcon color="action" />
                                            <Typography variant="h6" fontWeight={600}>Pager {pager}</Typography>
                                        </Stack>
                                    )}
                                    {formaSelecionada && (
                                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                            <Box sx={{ color: 'action.active', display: 'flex', pt: 0.25 }}>
                                                <IconePagamento forma={formaSelecionada.forma} />
                                            </Box>
                                            <Box>
                                                <Typography variant="h6" fontWeight={600}>{formaSelecionada.descricao}</Typography>
                                                {formaSelecionada.forma === 'DINHEIRO' && (
                                                    <Typography variant="body1" color="text.secondary">
                                                        {descreverTroco(trocoValor, subtotal)}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Stack>
                                    )}
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Total</Typography>
                                    <Typography variant="h4" sx={{ fontWeight: 900, color: 'success.main' }}>{formatarBRL(subtotal)}</Typography>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Stack>
                )

            default:
                return <></>
        }
    })()

    return (
        <>
            <Dialog
                open
                onClose={fecharParaCarrinho}
                fullScreen
                // Mesmo branco do checkout atrás do cabeçalho e da lateral do stepper; o passo tem o fundo dele.
                PaperProps={{ sx: { bgcolor: 'background.paper' } }}
            >
                <ScreenHeader
                    title="Finalizar pedido"
                    subTitle={`${totalItens} ${totalItens === 1 ? 'item' : 'itens'} · ${formatarBRL(subtotal)}`}
                    onClickBack={voltar}
                    logoUrl={loja?.configuracao.urlLogo ?? null}
                />

                <LayoutPassos
                    deitado={deitado}
                    stepper={<StepperPagamento rotulos={passosVisiveis.map((p) => ROTULO_PASSO_TOTEM[p])} ativo={passo} deitado={deitado} />}
                >
                    <Stack spacing={3} sx={{ flex: '1 0 auto', alignItems: 'center', justifyContent: 'center', p: 3 }}>
                        {avisoPreco && (
                            <Alert severity={avisoPreco.tom} onClose={descartarAviso} sx={{ fontSize: '1.05rem', width: '100%', maxWidth: 650 }}>
                                {avisoPreco.texto}
                            </Alert>
                        )}

                        {conteudo}

                        {erro && (
                            <Alert severity="error" sx={{ width: '100%', maxWidth: 650 }}>
                                {erro}
                            </Alert>
                        )}

                        {falhaEnvio && (
                            <Button
                                variant="outlined"
                                startIcon={<HomeIcon />}
                                onClick={() => reiniciarAtendimento(dispatch)}
                                sx={{ ...ESTILO_ACAO, width: '100%', maxWidth: 650 }}
                            >
                                Voltar ao início
                            </Button>
                        )}
                    </Stack>
                </LayoutPassos>

                <Box
                    data-teclado-acao
                    sx={{
                        p: 2,
                        borderTop: 1,
                        borderColor: 'divider',
                        flexShrink: 0,
                        backgroundColor: 'background.paper',
                        pb: 'calc(16px + env(safe-area-inset-bottom))',
                    }}
                >
                    <Box sx={{ mx: 'auto', width: '100%' }}>
                        {passo < ultimoPasso ? (
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                endIcon={<ChevronRightIcon />}
                                disabled={!passoValido(passoAtual, dados) && !perguntarTroco}
                                onClick={() => (perguntarTroco ? setTrocoAberto(true) : setPasso((p) => p + 1))}
                                sx={ESTILO_ACAO}
                            >
                                Continuar
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                fullWidth
                                size="large"
                                disabled={(!podeEnviar(dados, passosVisiveis) && !perguntarTroco) || enviando}
                                onClick={() => (perguntarTroco ? setTrocoAberto(true) : void confirmar())}
                                sx={ESTILO_ACAO}
                            >
                                {enviando ? <CircularProgress size={24} /> : 'Enviar pedido'}
                            </Button>
                        )}
                    </Box>
                </Box>
            </Dialog>

            {trocoAberto && (
                <DialogTroco
                    aberto
                    total={subtotal}
                    trocoInicial={trocoInput}
                    onFechar={() => setTrocoAberto(false)}
                    onOutraForma={() => {
                        setIdForma('')
                        setTrocoAberto(false)
                    }}
                    onConfirmar={(troco) => {
                        setTrocoInput(troco)
                        setTrocoRespondido(true)
                        setTrocoAberto(false)
                    }}
                />
            )}

            {tefAberto && (
                <DialogPagamentoTef
                    aberto
                    pedidoId={tefAberto.pedidoId}
                    valorCentavos={tefAberto.valorCentavos}
                    metodos={tefAberto.metodos}
                    onSucesso={() => {
                        const { pedidoId } = tefAberto
                        setTefAberto(null)
                        irVista(dispatch, { currentView: 'VIEW_CONFIRMADO', pedidoId })
                    }}
                    onCancelar={() => setTefAberto(null)}
                />
            )}
        </>
    )
}
